/**
 * Serviço de auditoria de mudanças de contexto
 */

import { supabase } from '@/integrations/supabase/client';

export type ContextAuditAction = 
  | 'CONTEXT_SWITCH_NETWORK'
  | 'CONTEXT_SWITCH_MODE'
  | 'CONTEXT_SWITCH_STORE'
  | 'ACTING_AS_STORE';

export interface ContextAuditMetadata {
  previousNetworkId?: string | null;
  previousStoreId?: string | null;
  previousContextMode?: 'NETWORK' | 'STORE';
  targetNetworkId?: string | null;
  targetStoreId?: string | null;
  targetContextMode?: 'NETWORK' | 'STORE';
  actingAsStoreId?: string | null;
  targetRoute?: string;
  userAgent?: string;
  ipAddress?: string;
}

/**
 * Registra uma ação de mudança de contexto na auditoria
 */
export const logContextChange = async (
  action: ContextAuditAction,
  networkId: string,
  storeId: string | null,
  metadata: ContextAuditMetadata
): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('Tentativa de registrar auditoria sem usuário autenticado');
      return;
    }

    // Obter informações adicionais do navegador
    const userAgent = navigator?.userAgent || 'Unknown';
    
    const auditData = {
      user_id: user.id,
      network_id: networkId,
      store_id: storeId,
      action,
      entity: 'context',
      entity_id: networkId, // ID da entidade principal (rede)
      acting_as_store_id: metadata.actingAsStoreId || null,
      user_agent: userAgent,
      metadata: {
        ...metadata,
        userAgent,
        timestamp: new Date().toISOString(),
      },
    };

    const { error } = await supabase
      .from('audit_logs')
      .insert([auditData]);

    if (error) {
      console.error('Erro ao registrar auditoria de contexto:', error);
    }
  } catch (error) {
    console.error('Erro ao criar log de auditoria:', error);
  }
};

/**
 * Registra mudança de rede
 */
export const logNetworkSwitch = async (
  previousNetworkId: string | null,
  targetNetworkId: string,
  targetRoute?: string
): Promise<void> => {
  await logContextChange(
    'CONTEXT_SWITCH_NETWORK',
    targetNetworkId,
    null,
    {
      previousNetworkId,
      targetNetworkId,
      targetRoute,
    }
  );
};

/**
 * Registra mudança de modo (Rede <-> Loja)
 */
export const logModeSwitch = async (
  networkId: string,
  previousMode: 'NETWORK' | 'STORE',
  targetMode: 'NETWORK' | 'STORE',
  storeId: string | null,
  targetRoute?: string
): Promise<void> => {
  await logContextChange(
    'CONTEXT_SWITCH_MODE',
    networkId,
    storeId,
    {
      previousContextMode: previousMode,
      targetContextMode: targetMode,
      targetStoreId: storeId,
      targetRoute,
    }
  );
};

/**
 * Registra mudança de loja
 */
export const logStoreSwitch = async (
  networkId: string,
  previousStoreId: string | null,
  targetStoreId: string,
  targetRoute?: string
): Promise<void> => {
  await logContextChange(
    'CONTEXT_SWITCH_STORE',
    networkId,
    targetStoreId,
    {
      previousStoreId,
      targetStoreId,
      targetRoute,
    }
  );
};

/**
 * Registra ação interloja
 */
export const logActingAsStore = async (
  networkId: string,
  currentStoreId: string,
  actingAsStoreId: string,
  action: string,
  targetRoute?: string
): Promise<void> => {
  await logContextChange(
    'ACTING_AS_STORE',
    networkId,
    currentStoreId,
    {
      actingAsStoreId,
      targetStoreId: currentStoreId,
      targetRoute,
    }
  );
};
